import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

// Interfaces
import { RootState } from "@/redux/store";
import { IPeriodTreeLeaf } from "@Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import { Nullable } from "@Common/interfaces/types.ts";

// Context
import { CRMContextProvider } from "@/DataPages/context/crm/CRMContext";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// API
import { onUpdateCustomers, onUpdateDispositions, onUpdateSalesLeads } from "@/DataPages/api/crm/subscriptions";

// Layouts
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import PayPeriodSideBar from "@Layouts/components/SideBars/PayPeriodSideBar";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import { useDataControl } from "@/Common/context/dataControl";
import CRMContainer from "./CRMContainer";
import { useLocationId } from "@/Common/hooks/Location/useLocationId";

// Constants
import { CRM_KEYS } from "./constants";

// Constants
const QUERY_IDENTIFIERS = Object.values(CRM_KEYS.QUERY);

export default function CRMLayer() {
	const { data: controlData, setData: setControlData } = useDataControl();
	const { clearCache } = useCacheCleaner();
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = useLocationId();
	// States
	const open = useRef(true);
	const lastSelected = useRef<Nullable<IPeriodTreeLeaf>>(null);
	const [selection, setSelection] = useState<Nullable<IPeriodTreeLeaf>>(null);

	const toggleOpenState = () => {
		open.current = !open.current;
	};

	const sideBar = useMemo(() => {
		const handleSelection = function (selected?: IPeriodTreeLeaf | null, isActiveCycle = false) {
			const commissionCycleId = selected?.cycleId;
			const payPeriodId = selected?.payPeriodId;
			if (selected?._id !== lastSelected.current?._id) {
				lastSelected.current = selected;
				setSelection(selected);
				toggleOpenState();
				if (isActiveCycle) {
					const { locationId } = location ?? {};
					addSubscription(
						[CRM_KEYS.SUBSCRIPTION.SALES_LEAD, locationId, commissionCycleId, payPeriodId],
						[
							{
								context: "onUpdateSalesLeads",
								query: onUpdateSalesLeads,
								variables: {
									commissionCycleId,
									payPeriodId,
								},
							},
						]
					);
					addSubscription(
						[CRM_KEYS.SUBSCRIPTION.DISPOSITION, locationId, commissionCycleId, payPeriodId],
						[
							{
								context: "onUpdateDispositions",
								query: onUpdateDispositions,
								variables: {
									commissionCycleId,
									payPeriodId,
								},
							},
						]
					);
					addSubscription(
						[CRM_KEYS.SUBSCRIPTION.CUSTOMER, locationId],
						[
							{
								context: "onUpdateCustomers",
								query: onUpdateCustomers,
								variables: {
									locationId,
								},
							},
						]
					);
				}
			}
		};

		return <PayPeriodSideBar callbackSelected={handleSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription, location]);

	useEffect(() => {
		if (!locationId) {
			return () => {};
		}
		return () => {
			(setControlData as (value: unknown) => void)({ panelDisabled: false });
			setSelection(null);
			open.current = true;
			lastSelected.current = null;
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
		};
	}, [locationId, clearSubscriptionContext]);

	const { panelDisabled = false } = (controlData as { panelDisabled: boolean }) ?? {};

	return (
		<CRMContextProvider selectionId={selection?._id ?? ""} locationId={location?.locationId ?? ""} selection={selection ?? null}>
			<BodyLayer
				floatingContent={<CollapsibleLeftPanel disabled={panelDisabled} permanentTrigger={open.current} control={sideBar} />}
			>
				<RoundedBox sx={{ minHeight: "630px", position: "relative" }} className="!px-0">
					<CRMContainer />
				</RoundedBox>
			</BodyLayer>
		</CRMContextProvider>
	);
}
